import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Clock, ChevronRight, ShoppingBag, Users, Truck, Award } from 'lucide-react';

const Home = () => {
  const [activeProduct, setActiveProduct] = useState(null);

  const products = [
  {
    name: "Artisan Sourdough",
    description: "Traditional sourdough with perfect crust and airy crumb",
    image: "https://images.unsplash.com/photo-1670843837159-ddae2ec115d4"
  },
  {
    name: "Rustic Baguettes",
    description: "Classic French baguettes baked fresh daily",
    image: "https://images.unsplash.com/photo-1534620808146-d33bb39128b2"
  },
  {
    name: "Whole Grain Breads",
    description: "Nutritious whole grain varieties for health-conscious customers",
    image: "https://images.unsplash.com/photo-1643369176535-139e9722209c"
  },
  {
    name: "Premium Croissants",
    description: "Buttery, flaky croissants made with premium ingredients",
    image: "https://images.unsplash.com/photo-1555507036-ab1f4038808a"
  },
  {
    name: "Specialty Rolls",
    description: "Perfect rolls for sandwiches and burgers",
    image: "https://images.pexels.com/photos/205961/pexels-photo-205961.jpeg"
  },
  {
    name: "Continental Breads",
    description: "Wide range of European-style breads",
    image: "https://images.pexels.com/photos/16125451/pexels-photo-16125451.jpeg"
  }];


  const features = [
  {
    icon: ShoppingBag,
    title: "Fresh Daily",
    description: "All products baked fresh every day and delivered to your doorstep"
  },
  {
    icon: Truck,
    title: "6 Day Delivery",
    description: "Reliable delivery service 6 days a week to ensure freshness"
  },
  {
    icon: Users,
    title: "B2B Focus",
    description: "Dedicated service for cafes, restaurants, hospitals, and caterers"
  },
  {
    icon: Award,
    title: "Quality Guaranteed",
    description: "Passionate about service and getting it right every time"
  }];


  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_patricks-bread-co/artifacts/dje6x0uj_logo.jpg" 
              alt="Patricks Breads Logo" 
              className="w-12 h-12 rounded-full object-cover"
            />
            <h1 className="text-2xl font-bold text-gray-900">Patricks Continental Breads</h1>
          </div>
          <nav className="hidden md:flex gap-8">
            <a href="#about" className="text-gray-700 hover:text-amber-700 transition-colors font-medium">About</a>
            <a href="#products" className="text-gray-700 hover:text-amber-700 transition-colors font-medium">Products</a>
            <a href="#contact" className="hover:text-amber-700 transition-colors !font-medium text-gray-700">02 8089 0783 | Orders@patricksbreads.com.au </a>
          </nav>
          <a href="tel:+61000000000">
            <Button className="bg-amber-700 hover:bg-amber-800 text-white">
              <Phone className="w-4 h-4 mr-2" />
              Call Us
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="!font-semibold !text-sm px-4 py-2 rounded-full text-amber-800 bg-amber-100">Wholesale Supplier Since 2010

                </span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Fresh Baked
                <span className="block text-amber-700">Every Day</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Premium wholesale supplier of freshly baked products delivered 6 days a week to cafes, restaurants, hospitals, and catering companies across the region.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#products">
                  <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white text-lg px-8">
                    View Products
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="#contact">
                  <Button size="lg" variant="outline" className="border-2 border-amber-700 text-amber-700 hover:bg-amber-50 text-lg px-8">
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl blur-2xl opacity-30"></div>
              <img
                src="https://images.unsplash.com/photo-1732565674273-44e3ba2823bc"
                alt="Fresh artisan bread"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover" />

            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) =>
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto">
                    <feature.icon className="w-8 h-8 text-amber-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1587241321921-91a834d6d191"
                alt="Professional bakery"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover" />

              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="!font-bold !text-4xl text-amber-700">15 + </p>
                <p className="text-gray-600 font-semibold">Years Experience</p>
              </div>
            </div>
            <div className="space-y-6 order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Welcome to Patricks Continental Breads
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Patricks Continental Breads is a wholesale supplier of freshly baked products, delivered 6 days a week. Ordered products are baked fresh every day and delivered to your doorstep for your convenience.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our clientele includes cafes, restaurants, hospitals, airports, catering companies and supermarkets. We source our products from enthusiastic suppliers, with an ability to meet current and potential capacity requirements, and do so on the desired delivery schedule.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-semibold text-amber-800">
                We are passionate about our service to our customers and get it right every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Our Products</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Fresh, high-quality baked goods perfect for your business needs
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) =>
            <Card
              key={index}
              className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              onMouseEnter={() => setActiveProduct(index)}
              onMouseLeave={() => setActiveProduct(null)}>

                <div className="relative h-64 overflow-hidden">
                  <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                  activeProduct === index ? 'scale-110' : 'scale-100'}`
                  } />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-2xl font-bold text-gray-900">{product.name}</h3>
                  <p className="text-gray-600">{product.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Get In Touch</h2>
            <p className="text-xl text-gray-600">
              Ready to order? Contact us today for seamless service
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <Card className="border-none shadow-lg p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Phone</h3>
                      <a href="tel:+61000000000" className="text-gray-600 hover:text-amber-700 transition-colors">02 8089 0783

                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                      <a href="mailto:info@patricksbread.com.au" className="text-gray-600 hover:text-amber-700 transition-colors">orders@patricksbreads.com.au

                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Location</h3>
                      <p className="text-gray-600">
                        Sydney, Australia
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Delivery Hours</h3>
                      <p className="text-gray-600">Monday - Saturday</p>
                      <p className="text-gray-600">Delivery to sydney , canberra, central coast and wollongong regions</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1599819055803-717bba43890f"
                alt="Fresh bread delivery"
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-amber-900/60 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-3xl font-bold mb-2">Order Fresh Today</h3>
                  <p className="text-lg text-amber-50">Experience the difference of daily fresh baked goods</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Order Online Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold">
                  Quick & Easy Ordering
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Order Online Now
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Scan the QR code or click below to place your order instantly. Fast, convenient, and secure ordering for all your wholesale bakery needs.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-700" />
                  </div>
                  <p className="text-gray-700">Browse our full product catalog</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-amber-700" />
                  </div>
                  <p className="text-gray-700">Place orders 24/7 at your convenience</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="">
                    <ChevronRight className="w-5 h-5 text-amber-700" />
                  </div>
                  <p className="text-gray-700"></p>
                </div>
              </div>
              <div className="pt-4">
                <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white text-lg px-8">
                  Start Ordering
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <Card className="p-8 shadow-2xl border-none bg-gradient-to-br from-white to-amber-50">
                <CardContent className="p-0 text-center space-y-6">
                  <div className="space-y-3">
                    <h3 className="text-2xl font-bold text-gray-900">Scan to Order</h3>
                    <p className="text-gray-600">Use your phone camera to scan</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl shadow-lg inline-block">
                    <img
                      src="https://customer-assets.emergentagent.com/job_patricks-bread-co/artifacts/a04vl288_download.png"
                      alt="Order Online QR Code"
                      className="w-64 h-64 object-contain" />

                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Point your camera at the QR code</p>
                    <p className="!font-semibold !text-sm text-amber-700">Ordermentum App </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-700 to-amber-900">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-amber-50">
            Join hundreds of satisfied businesses who trust Patricks Continental Breads for their daily supply
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+61000000000">
              <Button size="lg" className="bg-white text-amber-900 hover:bg-amber-50 text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
            <a href="mailto:info@patricksbread.com.au">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8">
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_patricks-bread-co/artifacts/dje6x0uj_logo.jpg" 
                  alt="Patricks Breads Logo" 
                  className="w-12 h-12 rounded-full object-cover"
                />
                <h3 className="text-xl font-bold">Patricks Continental Breads</h3>
              </div>
              <p className="text-gray-400">Wholesale supplier of fresh baked products since 2010

              </p>
            </div>
            <div className="space-y-4">
              <h4 className="text-lg font-bold">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                <a href="#about" className="text-gray-400 hover:text-amber-400 transition-colors">About Us</a>
                <a href="#products" className="text-gray-400 hover:text-amber-400 transition-colors">Products</a>
                <a href="#contact" className="text-gray-400 hover:text-amber-400 transition-colors">Contact</a>
              </nav>
            </div>
            <div className="space-y-4">
              <h4 className="text-lg font-bold">Contact Info</h4>
              <div className="space-y-2 text-gray-400">
                <p>Phone: 02 8089 0783</p>
                <p>Email: orders@patricksbreads.com.au</p>
                <p>Delivery: Mon-Sat</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Patricks Continental Breads. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;